import {
  CheckOutlined,
  ClearOutlined,
  CreateOutlined,
  ReplayOutlined,
  TaskAltOutlined,
  UnpublishedOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CellContext, ColumnDef, Row } from '@tanstack/react-table';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState, useTransition } from 'react';

import { CModalIds } from 'src/constants';
import { useIsRequestPending } from 'src/hooks';
import { useAppDispatch, useAppSelector } from 'src/redux_store';
import { openModal } from 'src/redux_store/common/modal_slice';
import { toastMessage } from 'src/redux_store/common/toast/toast_action';
import { approveData, editData } from 'src/redux_store/data/data_actions';
import { IDataItem, IPayloadApproveData } from 'src/types/data';
import { IIndicator } from 'src/types/indicators';

import config from 'src/config';
import { isHoiAn, isSitech, isSiTechSystem } from 'src/utils/function';
import ModalDeleteData from '../modal_delete_data';
import EditTable from './table';
import { ESystemName } from 'src/types/enum';
import { usePermission } from 'src/hooks/use_permission';
import { PermissionSchemes } from 'src/constants/role';
import ModalDeleteMultipleData from '../modal_delete_multiple_data';

type Props = {
  indicatorColumn: IIndicator[];
};
const notEdited = ['name', 'sentAt', 'STT', 'setting'];
const DataTable = ({ indicatorColumn }: Props) => {
  const { dataList, payloadData } = useAppSelector((state) => state.dataSlice);

  const dispatch = useAppDispatch();

  const [isPending] = useTransition();
  const isLoadingGetData = useIsRequestPending('myData', 'getData');
  const isLoadingApproveData = useIsRequestPending('myData', 'approveData');
  const [editableRowId, setEditableRowId] = React.useState<number | null>(null);
  const [columnsIndicators, setColumnsIndicators] = useState<ColumnDef<IDataItem>[]>([]);
  const [detailsArray, setDetailsArray] = React.useState<{ indicator: string; value: number }[]>(
    [],
  );

  const [checked, setChecked] = useState<{ bucketId: string; dataId: string }[]>([]);

  const onCheck = (bucketId: string, dataId: string) => {
    const isExist = !!checked.find((item) => item.dataId === dataId && item.bucketId === bucketId);

    if (isExist) {
      setChecked(checked.filter((item) => item.dataId !== dataId));
    } else {
      setChecked([...checked, { bucketId, dataId }]);
    }
  };

  const hasPermissionUpdateData = usePermission([PermissionSchemes.editData]);
  const hasPermissionDeleteData = usePermission([PermissionSchemes.deleteData]);
  const hasPermissionApproveData = usePermission([PermissionSchemes.approveData]);

  const handleOpenEditInfo = (row: Row<IDataItem>) => {
    const data: Array<{ indicator: string; value: number }> = [];
    const columns: string[] = [];

    columnsIndicators.forEach((item) => {
      if (item.id) columns.push(item.id);
    });

    Object.entries(row.original.data).forEach(([indicator, value]) => {
      data.push({ indicator, value });
    });

    const diffColumns = _.differenceWith(columns, data, (x, y) => {
      return x.toUpperCase() === y.indicator.toUpperCase();
    });

    diffColumns.forEach((key: string) => {
      data.push({ indicator: key, value: 0 });
    });

    setDetailsArray(data);
    setEditableRowId(row.index);
  };

  const handleCloseEditInfo = () => {
    setEditableRowId(null);
  };

  const getUnit = (indicatorSymbol: string) => {
    const found = indicatorColumn.find((item) => item.symbol === indicatorSymbol);
    return found ? found.unit : '';
  };

  const handleEditInfo = (row: Row<IDataItem>) => {
    const newPayload = {
      bucketId: row.original.bucketId,
      dataId: row.original.id,
      ...(isSitech ? { stationId: payloadData.stationId } : {}),
      details: [...detailsArray].map(({ indicator, value }) => {
        return {
          indicator,
          value,
          ...(isSitech || isHoiAn ? { unit: getUnit(indicator) } : {}),
        };
      }),
    };

    dispatch(editData(newPayload))
      .unwrap()
      .then(() => {
        toastMessage.success('Cập nhật dữ liệu thành công');
        handleCloseEditInfo();
      });
  };

  const handleApproveInfo = (row: Row<IDataItem>) => {
    const newPayload: IPayloadApproveData = {
      approved: !row.original.approved,
      bucketId: row.original.bucketId,
      dataId: row.original.id,
      ...(isSitech ? { stationId: payloadData.stationId } : {}),
    };
    dispatch(approveData(newPayload))
      .unwrap()
      .then(() => {
        if (row.original.approved == false) {
          toastMessage.success('Duyệt dữ liệu thành công');
        } else {
          toastMessage.success('Huỷ duyệt dữ liệu thành công');
        }
      });
  };
  const handleDeleteInfo = async (row: Row<IDataItem>) => {
    const newPayload = {
      bucketId: row.original.bucketId,
      dataId: row.original.id,
      ...(isSitech ? { stationId: payloadData.stationId } : {}),
    };
    dispatch(
      openModal({
        modalId: CModalIds.deleteData,
        modalComponent: <ModalDeleteData modalId={CModalIds.deleteData} payload={newPayload} />,
      }),
    );
  };

  const defaultEditColumn: Partial<ColumnDef<IDataItem>> = useMemo(() => {
    return {
      cell: ({ row, column }) => {
        const newName = column.id?.trim()?.toUpperCase();
        const initialValue = row.original.dataUpper[newName] || 0;

        const [value, setValue] = React.useState<number>(initialValue);

        const onBlur = () => {
          const arr = [...detailsArray];
          for (const item of arr) {
            if (item.indicator.toUpperCase() === newName) {
              item.value = value;

              setDetailsArray(arr);
              return;
            }
          }
        };

        if (!notEdited.includes(column.id) && row.index === editableRowId) {
          return (
            <TextField
              type="number"
              variant="standard"
              sx={{ p: 0 }}
              value={value as number}
              onChange={(e) => setValue(Number(e.target.value))}
              onBlur={onBlur}
            />
          );
        }
        return value;
      },
    };
  }, [editableRowId]);

  const checkIsSelectAll = () => {
    if (checked.length === 0) return false;

    const formatChecked = checked.map((item) => item.dataId);
    return dataList.data.every((item) => {
      const result = formatChecked.includes(item.id);

      return !!result;
    });
  };

  const checkIsEmptyChecked = () => {
    if (checked.length === 0) return true;
    return dataList.data.every((item) => !checked.some((check) => check.dataId === item.id));
  };

  const checkHasSomeChecked = () => {
    if (checked.length === 0) return false;

    return (
      !checkIsEmptyChecked() &&
      !checkIsSelectAll() &&
      dataList.data.some((item) => checked.find((check) => check.dataId === item.id))
    );
  };

  const columns: ColumnDef<IDataItem>[] = useMemo(() => {
    const finalColumn: ColumnDef<IDataItem>[] = [
      ...(![ESystemName.SITECH, ESystemName.HOIAN].includes(config.systemName)
        ? [
            {
              id: 'setting',
              header: 'Tuỳ chọn',
              size: 100,
              cell: (info: CellContext<IDataItem, unknown>) => {
                return info.row.index === editableRowId ? (
                  <Box>
                    <Tooltip title="Cập nhật">
                      <IconButton
                        color="success"
                        onClick={() => {
                          handleEditInfo(info.row);
                        }}
                      >
                        <CheckOutlined />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title="Khôi phục"
                      onClick={() => {
                        handleCloseEditInfo();
                      }}
                    >
                      <IconButton color="error">
                        <ReplayOutlined />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box>
                    {hasPermissionUpdateData && (
                      <Tooltip title="Chỉnh sửa">
                        <IconButton
                          color="success"
                          onClick={() => {
                            handleOpenEditInfo(info.row);
                          }}
                        >
                          <CreateOutlined />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermissionApproveData && (
                      <>
                        {info.row.original.approved === true ? (
                          <Tooltip title="Đã Duyệt">
                            <IconButton
                              color="warning"
                              onClick={() => {
                                handleApproveInfo(info.row);
                              }}
                              disabled={isLoadingApproveData}
                            >
                              <TaskAltOutlined />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Chưa duyệt">
                            <IconButton
                              color="warning"
                              onClick={() => {
                                handleApproveInfo(info.row);
                              }}
                              disabled={isLoadingApproveData}
                            >
                              <UnpublishedOutlined />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {hasPermissionDeleteData && (
                      <Tooltip title="Xóa">
                        <IconButton
                          color="error"
                          onClick={() => {
                            handleDeleteInfo(info.row);
                          }}
                        >
                          <ClearOutlined />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              },
            },
          ]
        : []),
      ...(isSitech
        ? [
            {
              id: 'checkbox',
              header: () => {
                return (
                  <Checkbox
                    checked={checkIsSelectAll()}
                    indeterminate={checkHasSomeChecked()}
                    onChange={() => {
                      if (checkIsSelectAll()) {
                        // uncheck all
                        setChecked((prev) =>
                          prev.filter((item) => {
                            const index = dataList.data.findIndex(
                              (data) => data.id === item.dataId,
                            );
                            return index === -1;
                          }),
                        );
                        return;
                      }

                      if (checkHasSomeChecked()) {
                        // check all
                        const newChecked: { bucketId: string; dataId: string }[] = [...checked];
                        dataList.data.forEach((item) => {
                          const isExisted = checked.find((check) => check.dataId === item.id);
                          if (!isExisted) {
                            newChecked.push({ bucketId: item.bucketId, dataId: item.id });
                          }
                        });
                        setChecked(newChecked);
                        return;
                      }

                      // check all
                      setChecked((prev) => [
                        ...prev,
                        ...dataList.data.map((item) => {
                          return { bucketId: item.bucketId, dataId: item.id };
                        }),
                      ]);
                    }}
                  />
                );
              },

              size: 50,
              cell: ({ row }: any) => {
                const { id, bucketId } = row.original;
                return (
                  <Checkbox
                    checked={checked.some(
                      (item) => item.dataId === id && item.bucketId === bucketId,
                    )}
                    onChange={() => onCheck(bucketId, id)}
                  />
                );
              },
            },
          ]
        : []),
      {
        id: 'STT',
        header: 'STT',
        size: 20,
        maxSize: 20,
        cell: ({ row }) => (
          <Typography textAlign={isSiTechSystem ? 'center' : undefined}>
            {(payloadData.page - 1) * payloadData.limit + (row.index + 1)}
          </Typography>
        ),
      },
      {
        id: 'name',
        accessorKey: 'name',
        size: 200,
        header: 'Tên trạm',
        cell: ({ row }) => row.original.name,
      },
      {
        id: 'sentAt',
        header: 'Thời gian',
        accessorKey: 'sentAt',
        cell: ({ row }) => moment(row.original.sentAt).utc(false).format('DD-MM-YYYY HH:mm:ss'),
      },
      ...(isSiTechSystem &&
      (hasPermissionUpdateData || hasPermissionApproveData || hasPermissionDeleteData)
        ? [
            {
              id: 'setting',
              header: 'Tuỳ chọn',
              size: 200,
              cell: (info: any) => {
                return info.row.index === editableRowId ? (
                  <Box>
                    <Tooltip title="Cập nhật">
                      <IconButton
                        color="success"
                        onClick={() => {
                          handleEditInfo(info.row);
                        }}
                      >
                        <CheckOutlined />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title="Khôi phục"
                      onClick={() => {
                        handleCloseEditInfo();
                      }}
                    >
                      <IconButton color="error">
                        <ReplayOutlined />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Box>
                    {hasPermissionUpdateData && (
                      <Tooltip title="Chỉnh sửa">
                        <IconButton
                          color="success"
                          onClick={() => {
                            console.log(info);
                            handleOpenEditInfo(info.row);
                          }}
                        >
                          <CreateOutlined />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasPermissionApproveData && (
                      <>
                        {info.row.original.approved === true ? (
                          <Tooltip title="Đã Duyệt">
                            <IconButton
                              color="warning"
                              onClick={() => {
                                handleApproveInfo(info.row);
                              }}
                              disabled={isLoadingApproveData}
                            >
                              <TaskAltOutlined />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Chưa duyệt">
                            <IconButton
                              color="warning"
                              onClick={() => {
                                handleApproveInfo(info.row);
                              }}
                              disabled={isLoadingApproveData}
                            >
                              <UnpublishedOutlined />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {hasPermissionDeleteData && (
                      <Tooltip title="Xóa">
                        <IconButton
                          color="error"
                          onClick={() => {
                            handleDeleteInfo(info.row);
                          }}
                        >
                          <ClearOutlined />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              },
            },
          ]
        : []),
    ];

    const columnsIndicator: ColumnDef<IDataItem, unknown>[] = indicatorColumn.map((item) => {
      const columnIndicatorItem = {
        id: item.symbol,
        size: 150,
        accessorKey: `dataUpper.${item.symbol.toUpperCase()}`,
        header: () => <span>{`${item.symbol} (${item.unit})`}</span>,
      };

      return columnIndicatorItem;
    });

    setColumnsIndicators(columnsIndicator);
    const columnTable = finalColumn.concat(columnsIndicator);

    return columnTable;
  }, [editableRowId, indicatorColumn, payloadData, checked, dataList.data]);

  const onCancelDelete = () => {
    setChecked([]);
  };

  const onDelete = () => {
    dispatch(
      openModal({
        modalId: CModalIds.deleteMultipleData,
        modalComponent: (
          <ModalDeleteMultipleData
            modalId={CModalIds.deleteMultipleData}
            payload={checked}
            callback={onCancelDelete}
            stationId={payloadData.stationId}
          />
        ),
      }),
    );
  };

  useEffect(() => {
    if (payloadData.stationId) {
      setChecked([]);
    }
  }, [payloadData.stationId]);

  return (
    <Grid direction="column" container>
      {checked.length > 0 && (
        <Grid gap={3} alignItems="center" container p={1} mb={1} bgcolor="#fff">
          <Box width={150}>
            <Typography fontWeight={600} fontSize={16}>
              Đã chọn {checked.length} dữ liệu
            </Typography>
          </Box>
          <Box>
            <Grid gap={1} container>
              <Button variant="contained" color="error" onClick={onDelete}>
                Xoá
              </Button>
              <Button onClick={onCancelDelete} variant="outlined">
                Huỷ
              </Button>
            </Grid>
          </Box>
        </Grid>
      )}
      <EditTable<IDataItem>
        data={dataList.data}
        isLoading={isPending || isLoadingGetData}
        columns={columns}
        defaultEditColumn={defaultEditColumn}
        size="small"
      />
    </Grid>
  );
};
export default DataTable;
